import {
  CameraInputMode,
  type PreviewStartOptions,
  type PreviewStartPayload,
  type SnapshotCaptureResult,
} from '@peritasai/types';
import { getCameraApiUrl } from './camera-api-url';

type BaseUrlResolver = () => string;

const SNAPSHOT_URL_KEYS = [
  'snapshotUrl',
  'snapshot_url',
  'imageUrl',
  'image_url',
] as const;

function isLikelySnapshotPath(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) return false;
  if (/^https?:\/\//i.test(trimmed)) return true;
  if (trimmed.startsWith('/')) return true;
  return /\.(jpg|jpeg|png|webp|bmp|gif)$/i.test(trimmed);
}

function normalizeUrl(value: string, baseUrl: string): string {
  const trimmed = value.trim();
  if (!trimmed) return '';
  if (!isLikelySnapshotPath(trimmed)) return '';

  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }

  if (trimmed.startsWith('/')) {
    return `${baseUrl}${trimmed}`;
  }

  return `${baseUrl}/${trimmed}`;
}

function extractSnapshotUrl(raw: unknown, baseUrl: string): string | null {
  if (typeof raw === 'string') {
    const normalized = normalizeUrl(raw, baseUrl);
    return normalized || null;
  }

  if (!raw || typeof raw !== 'object') {
    return null;
  }

  const record = raw as Record<string, unknown>;
  for (const key of SNAPSHOT_URL_KEYS) {
    const candidate = record[key];
    if (typeof candidate === 'string') {
      const normalized = normalizeUrl(candidate, baseUrl);
      if (normalized) return normalized;
    }
  }

  return null;
}

function normalizeStorageKey(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function buildPreviewPayload(options?: PreviewStartOptions): PreviewStartPayload {
  if (!options || options.inputMode === CameraInputMode.CAMERA) {
    return { kind: CameraInputMode.CAMERA };
  }

  const sourceValue = options.sourcePath?.trim();
  if (!sourceValue) {
    throw new Error('Video source filename is required to start preview');
  }

  const filename = sourceValue.split(/[\\/]/).filter(Boolean).pop();
  if (!filename) {
    throw new Error('Video source filename is required to start preview');
  }

  return { kind: CameraInputMode.VIDEO, filename };
}

/**
 * Client for the stateless camera-service endpoints used by the OEM tray assembly
 * flow. Standalone service (not periverse-api): plain `fetch`, no bearer token,
 * base URL from {@link getCameraApiUrl}.
 */
export class CameraApiClient {
  constructor(private readonly resolveBaseUrl: BaseUrlResolver = getCameraApiUrl) {}

  private get baseUrl(): string {
    return this.resolveBaseUrl().replace(/\/+$/, '');
  }

  private async request(path: string, init?: RequestInit): Promise<Response> {
    const response = await fetch(`${this.baseUrl}${path}`, init);
    if (!response.ok) {
      throw new Error(`Camera API request failed: ${response.status}`);
    }
    return response;
  }

  /**
   * `POST /preview/start` — begin a preview and return the MJPEG stream URL
   * (`{base}/mjpeg?nonce=…`). The nonce forces `<img>` to reload on restart.
   */
  async startPreview(options?: PreviewStartOptions): Promise<string> {
    const payload = buildPreviewPayload(options);
    await this.request('/preview/start', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return `${this.baseUrl}/mjpeg?nonce=${Date.now()}`;
  }

  /** `POST /preview/stop` — stop the active preview. */
  async stopPreview(): Promise<void> {
    await this.request('/preview/stop', { method: 'POST' });
  }

  /**
   * `GET /snapshot` — capture the current frame. The endpoint may reply with JSON,
   * a raw image, or a URL as text; all three are normalized to a displayable URL
   * plus the optional storage key from the `x-additional-storage-paths` header.
   */
  async takeSnapshot(trayAssemblyId?: string): Promise<SnapshotCaptureResult> {
    const trimmedAssemblyId = trayAssemblyId?.trim() ?? '';
    const query = trimmedAssemblyId
      ? `?tray_assembly_id=${encodeURIComponent(trimmedAssemblyId)}`
      : '';
    const response = await this.request(`/snapshot${query}`);
    const contentType = response.headers.get('content-type') ?? '';
    const headerStorageKey = normalizeStorageKey(
      response.headers.get('x-additional-storage-paths')
    );

    if (contentType.includes('application/json')) {
      const data = await response.json().catch(() => null);
      const url = extractSnapshotUrl(data, this.baseUrl);
      if (url) {
        return { previewUrl: url, storageKey: headerStorageKey };
      }
      throw new Error('Snapshot URL is missing from camera response');
    }

    if (contentType.toLowerCase().startsWith('image/')) {
      const blob = await response.blob();
      if (blob.size > 0) {
        return {
          previewUrl: URL.createObjectURL(blob),
          storageKey: headerStorageKey,
        };
      }
      throw new Error('Snapshot image payload is empty');
    }

    const blobFallbackResponse = response.clone();
    const text = (await response.text()).trim();
    const url = extractSnapshotUrl(text, this.baseUrl);
    if (url) {
      return { previewUrl: url, storageKey: headerStorageKey };
    }

    const blob = await blobFallbackResponse.blob();
    if (blob.size > 0) {
      return {
        previewUrl: URL.createObjectURL(blob),
        storageKey: headerStorageKey,
      };
    }

    throw new Error('Snapshot URL is missing from camera response');
  }
}

export const createCameraApiClient = (
  resolveBaseUrl: BaseUrlResolver = getCameraApiUrl
): CameraApiClient => new CameraApiClient(resolveBaseUrl);

export const cameraApiClient = new CameraApiClient();
